import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { MessageContent } from '../components/chat/MessageContent';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, 
  Send, 
  Bot, 
  User,
  Loader2,
  MicOff,
  MessageCircle,
  Check,
  CheckCheck,
  Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppChat() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      const existingSession = localStorage.getItem('whatsapp_session_id');
      if (existingSession) {
        setSessionId(existingSession);
        // Load previous messages
        try {
          const response = await axios.get(`${API}/whatsapp/history/${existingSession}`);
          if (response.data.messages) {
            setMessages(response.data.messages);
          }
        } catch (error) {
          console.log('No previous messages found');
        }
      } else {
        const newSession = `wa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setSessionId(newSession);
        localStorage.setItem('whatsapp_session_id', newSession);
      }
    };
    initSession();
  }, []);

  // Add welcome message
  useEffect(() => {
    if (sessionId && messages.length === 0) {
      const welcomeMessage = {
        id: 'welcome',
        type: 'bot',
        content: language === 'sw' 
          ? '🇹🇿 Karibu! Mimi ni mshauri wako wa uwekezaji wa AI. Ninaweza kukusaidia:\n\n• Kuelewa bidhaa za DSE (hisa, bondi, T-bills)\n• Kulinganisha chaguzi za uwekezaji\n• Kukokotoa mapato yanayotarajiwa\n• Kujifunza kuhusu usimamizi wa hatari\n\nUliza swali lolote la uwekezaji!'
          : '🇹🇿 Welcome! I\'m your AI Investment Coach. I can help you:\n\n• Understand DSE products (stocks, bonds, T-bills)\n• Compare investment options\n• Calculate expected returns\n• Learn about risk management\n\nAsk me any investment question!',
        timestamp: new Date().toISOString(),
        status: 'read'
      };
      setMessages([welcomeMessage]);
    }
  }, [sessionId, language, messages.length]);

  const sendMessage = useCallback(async (override) => {
    const text = (typeof override === 'string' ? override : inputMessage).trim();
    if (!text || isLoading) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      status: 'sent'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Update status to delivered
    setTimeout(() => {
      setMessages(prev => prev.map(m => 
        m.id === userMessage.id ? {...m, status: 'delivered'} : m
      ));
    }, 500);

    try {
      const response = await axios.post(`${API}/whatsapp/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        language: language
      });

      // Update user message status to read
      setMessages(prev => prev.map(m => 
        m.id === userMessage.id ? {...m, status: 'read'} : m
      ));

      const botMessage = {
        id: `bot_${Date.now()}`,
        type: 'bot',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        status: 'read'
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      toast.error(t('Failed to send message', 'Imeshindwa kutuma ujumbe'));
      setMessages(prev => prev.map(m => 
        m.id === userMessage.id ? {...m, status: 'failed'} : m
      ));
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, sessionId, language, t]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const MessageStatus = ({ status }) => {
    if (status === 'sent') return <Check className="w-3 h-3 text-muted-foreground" />;
    if (status === 'delivered') return <CheckCheck className="w-3 h-3 text-muted-foreground" />;
    if (status === 'read') return <CheckCheck className="w-3 h-3 text-blue-500" />;
    return null;
  };

  const suggestedQuestions = language === 'sw' ? [
    "Nifanyeje kuanza kuwekeza DSE?",
    "Tofauti kati ya hisa na bondi ni nini?",
    "UTT funds ni nini?",
    "Nawezaje kuhesabu faida yangu?"
  ] : [
    "How do I start investing in DSE?",
    "What's the difference between stocks and bonds?",
    "What are UTT funds?",
    "How can I calculate my returns?"
  ];

  return (
    <Layout>
      <div className="page-enter h-[calc(100vh-120px)] flex flex-col">
        {/* WhatsApp-style Header */}
        <div className="bg-primary text-primary-foreground p-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-primary-foreground hover:bg-primary-foreground/10"
              data-testid="whatsapp-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold text-base truncate" data-testid="whatsapp-chat-title">
                {t('Wekeza-Smart Coach', 'Mshauri wa Wekeza-Smart')}
              </h1>
              <p className="text-xs text-primary-foreground/70 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                {t('Online', 'Mtandaoni')}
              </p>
            </div>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-3"
          style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23E5DDD5\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundColor: '#ECE5DD'
          }}
        >
          {/* Date Separator */}
          <div className="flex justify-center">
            <span className="bg-white/80 text-xs text-muted-foreground px-3 py-1 rounded-lg shadow-sm">
              {t('Today', 'Leo')}
            </span>
          </div>

          {messages.map((message) => (
            <div 
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 shadow-sm ${
                  message.type === 'user' 
                    ? 'bg-[#DCF8C6] text-foreground rounded-br-none' 
                    : 'bg-white text-foreground rounded-bl-none'
                }`}
              >
                {message.type === 'bot' && (
                  <div className="flex items-center gap-1 mb-1">
                    <Sparkles className="w-3 h-3 text-primary" />
                    <span className="text-xs font-medium text-primary">AI Coach</span>
                  </div>
                )}
                <MessageContent content={message.content} isUser={message.type === 'user'} />
                <div className={`flex items-center gap-1 mt-1 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <span className="text-[10px] text-muted-foreground">
                    {formatTime(message.timestamp)}
                  </span>
                  {message.type === 'user' && <MessageStatus status={message.status} />}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white rounded-lg p-3 shadow-sm rounded-bl-none">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions (only show when no messages or few messages) */}
        {messages.length <= 1 && (
          <div className="px-4 py-2 bg-muted/30">
            <p className="text-xs text-muted-foreground mb-2">
              {t('Suggested questions:', 'Maswali yanayopendekezwa:')}
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(question)}
                  data-testid={`whatsapp-suggestion-${i}`}
                  className="text-xs bg-white border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-3 bg-[#F0F0F0] flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('Type a message...', 'Andika ujumbe...')}
              className="rounded-full bg-white pr-10"
              disabled={isLoading}
              data-testid="whatsapp-message-input"
            />
          </div>
          <Button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="rounded-full h-10 w-10 p-0 bg-primary hover:bg-primary/90"
              data-testid="whatsapp-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
        </div>
      </div>
    </Layout>
  );
}
